/** Pillars — used on Home paths and as Services filters. */
export const OFFERINGS = {
  lead: {
    id: 'lead',
    title: 'Lead',
    tagline: 'Navigate complexity with clarity and conviction.',
    blurb:
      'Executive coaching and fractional leadership when the top of the house needs a steady hand.',
    whoFor:
      'CEOs, COOs, CIOs, emerging executives, and leadership teams who need clearer decisions and stronger alignment.',
    inScope: [
      '1:1 executive coaching for CEOs, COOs, CIOs, and emerging leaders',
      'Fractional COO / CIO / CTO leadership',
      'C-suite team coaching and alignment workshops',
      'Organizational culture and leadership transformation',
      'Executive readiness and mentorship for rising leaders',
    ],
    outOfScope: [
      'Hands-on software engineering or product build (we introduce partners)',
      'Full-time permanent executive placement / search firm work',
      'Day-to-day HR administration',
      'Legal, audit, or compliance certification services',
    ],
    howStart:
      'A focused discovery conversation, then a defined coaching or fractional engagement with clear cadence and outcomes.',
  },
  transform: {
    id: 'transform',
    title: 'Transform',
    tagline: 'Unlock potential and build what is next.',
    blurb:
      'Operating-model and organizational change that sticks — grounded in decades of healthcare and life-sciences execution.',
    whoFor:
      'Organizations under pressure to improve how they operate, deliver, and perform — especially in healthcare and complex regulated environments.',
    inScope: [
      'Operational assessments for hospitals, health systems, and complex enterprises',
      'Lean process redesign for clinical and administrative workflows',
      'Organizational restructuring and transformation roadmapping',
      'Patient access, throughput, and experience initiatives',
      'Revenue cycle and financial performance improvement guidance',
      'Executive advisory on data, analytics, and modernization priorities',
    ],
    outOfScope: [
      'Custom application development and cloud engineering (partner delivery)',
      'Enterprise AI build / data platform implementation as a product shop',
      'Staffing large delivery teams under the 1Goss brand',
      'Guaranteed clinical or financial outcomes without operational ownership',
    ],
    howStart:
      'An assessment-led engagement that clarifies the operating problem, then a practical roadmap with executive sponsorship.',
  },
  govern: {
    id: 'govern',
    title: 'Govern',
    tagline: 'Strengthen accountability and steward legacy.',
    blurb:
      'Board counsel, governance, and speaking — with clear boundaries and serious presence.',
    whoFor:
      'Boards, advisory councils, and organizations seeking governance strength or a keynote voice on leadership and transformation.',
    inScope: [
      'Corporate, nonprofit, and advisory board service',
      'Governance, risk oversight, and organizational counsel',
      'Strategic guidance for healthcare and technology companies',
      'Speaking engagements on leadership, healthcare, and transformation',
      'Executive mentorship tied to board and leadership excellence',
    ],
    outOfScope: [
      'Legal counsel or fiduciary services outside board/advisory roles',
      'Lobbying or political campaign work',
      'Open-ended “fractional everything” without a defined mandate',
      'Technology build or managed services',
    ],
    howStart:
      'A conversation on mandate and fit — board seat, advisory role, or speaking — then a clear scope letter.',
  },
};

export const OFFERING_LIST = [OFFERINGS.lead, OFFERINGS.transform, OFFERINGS.govern];

export const NEED_IDS = ['lead', 'transform', 'govern'];

/** Quick-read counsel cards under each filter — open the stone modal. */
export const COUNSEL_OFFERS = [
  {
    slug: 'executive-coaching',
    need: 'lead',
    title: 'Executive coaching',
    blurb: '1:1 counsel for CEOs, COOs, CIOs, and rising leaders facing high-stakes decisions.',
    inScope: [
      'Confidential 1:1 coaching cadence',
      'Decision clarity under pressure',
      'Leadership presence and communication',
      'Stakeholder and board readiness',
      'Personal operating rhythm',
    ],
    outOfScope: [
      'Therapy or clinical counseling',
      'Performance management of your staff',
      'Ghostwriting or PR representation',
      'Hands-on product or engineering work',
    ],
    howStart:
      'A focused discovery conversation, then a defined coaching engagement with clear cadence and outcomes.',
  },
  {
    slug: 'fractional-coo-cio',
    need: 'lead',
    title: 'Fractional COO / CIO leadership',
    blurb:
      'Executive operational and technology leadership that brings structure, clarity, and scalable systems.',
    inScope: [
      'Operational strategy & execution',
      'Technology strategy & roadmaps',
      'Team leadership & development',
      'Process design & optimization',
      'Vendor & partner management',
      'Executive reporting & KPIs',
    ],
    outOfScope: [
      'Day-to-day task execution as staff augmentation',
      'Full-time permanent placement',
      'Transactional bookkeeping',
      'Legal or compliance certification',
      'Public relations or marketing',
      'Recruiting & HR administration',
    ],
    howStart:
      'We begin with a focused discovery to understand your priorities, challenges, and desired outcomes — then build a tailored plan.',
  },
  {
    slug: 'c-suite-alignment',
    need: 'lead',
    title: 'C-suite alignment',
    blurb: 'Team coaching and workshops that get the top of the house rowing in one direction.',
    inScope: [
      'Leadership team offsites and workshops',
      'Role clarity and decision rights',
      'Culture and operating norms',
      'Conflict and trust repair facilitation',
      'Succession and readiness conversations',
    ],
    outOfScope: [
      'Org-wide culture programs without executive sponsorship',
      'HR policy authorship',
      'Union negotiation or labor law counsel',
      'Technology build delivery',
    ],
    howStart:
      'A short diagnostic with the sponsor, then a facilitated working session with clear follow-through.',
  },
  {
    slug: 'strategy-to-impact',
    need: 'transform',
    title: 'Strategy to Impact',
    blurb: 'Align strategy with execution to drive measurable results.',
    inScope: [
      'Strategy-to-execution translation',
      'Transformation roadmapping',
      'Executive sponsorship design',
      'Priority and portfolio clarity',
      'Milestone and outcome tracking',
    ],
    outOfScope: [
      'Custom software development (partner delivery)',
      'Enterprise AI platform build as a product shop',
      'Guaranteed financial outcomes without operational ownership',
      'Large staffing under the 1Goss brand',
    ],
    howStart:
      'An assessment-led engagement that clarifies the operating problem, then a practical roadmap with executive sponsorship.',
  },
  {
    slug: 'operating-excellence',
    need: 'transform',
    title: 'Operating Excellence',
    blurb: 'Optimize processes and capabilities to unlock performance.',
    inScope: [
      'Operational assessments for complex enterprises',
      'Lean process redesign',
      'Throughput, access, and experience initiatives',
      'Revenue cycle and performance guidance',
      'Capability and org design for delivery',
    ],
    outOfScope: [
      'Hands-on coding or cloud engineering',
      'Managed services / BPO',
      'Clinical practice of medicine',
      'Audit attestation or certification',
    ],
    howStart:
      'A focused operating assessment, then a redesign sprint with clear owners and measures.',
  },
  {
    slug: 'change-adoption',
    need: 'transform',
    title: 'Change & Adoption',
    blurb: 'Engage people and build momentum for lasting change.',
    inScope: [
      'Change narrative and sponsorship',
      'Adoption planning for major initiatives',
      'Leader and manager enablement',
      'Stakeholder alignment workshops',
      'Resistance diagnosis and course-correction',
    ],
    outOfScope: [
      'Internal communications agency retainers',
      'LMS content production at scale',
      'Staff augmentation for PMO roles',
      'Technology implementation as the build shop',
    ],
    howStart:
      'Clarify the change mandate with sponsors, then a practical adoption plan tied to real milestones.',
  },
  {
    slug: 'board-advisory',
    need: 'govern',
    title: 'Board & advisory service',
    blurb: 'Board and advisory presence with serious judgment and clear boundaries.',
    inScope: [
      'Corporate, nonprofit, and advisory board service',
      'Strategic guidance for healthcare and technology companies',
      'Executive mentorship tied to board excellence',
      'Preparation for board-level conversations',
    ],
    outOfScope: [
      'Legal counsel or formal fiduciary services outside the seat',
      'Lobbying or political campaign work',
      'Open-ended fractional work without a mandate',
      'Technology build or managed services',
    ],
    howStart:
      'A conversation on mandate and fit — board seat or advisory role — then a clear scope letter.',
  },
  {
    slug: 'governance-counsel',
    need: 'govern',
    title: 'Governance counsel',
    blurb: 'Strengthen accountability, oversight, and organizational stewardship.',
    inScope: [
      'Governance and risk oversight counsel',
      'Committee and reporting cadence design',
      'Accountability frameworks for leaders',
      'Organizational stewardship conversations',
    ],
    outOfScope: [
      'Legal opinions or regulatory filings',
      'Internal audit outsourcing',
      'Compliance certification programs',
      'Day-to-day management of the enterprise',
    ],
    howStart:
      'A fit conversation on the governance problem, then a defined counsel engagement.',
  },
  {
    slug: 'speaking',
    need: 'govern',
    title: 'Speaking & keynotes',
    blurb: 'A clear voice on leadership, healthcare, and transformation.',
    inScope: [
      'Keynotes and executive briefings',
      'Panel participation and facilitation',
      'Leadership and transformation themes',
      'Healthcare and regulated-industry context',
    ],
    outOfScope: [
      'Open-ended content marketing retainers',
      'Ghostwritten thought-leadership mills',
      'Political campaign speaking',
      'Product demos for third-party vendors',
    ],
    howStart:
      'Share the audience, theme, and format — we confirm fit and deliver a focused brief.',
  },
];

export function counselOffersForNeed(needId) {
  return COUNSEL_OFFERS.filter((o) => o.need === needId);
}

export function counselOfferBySlug(slug) {
  return COUNSEL_OFFERS.find((o) => o.slug === slug);
}
